import { formatCourt, joursEntre } from './dates.js';
import { blocAdresseLivraison } from './commande-fournisseur.js';
import { echapperHtml, SIGNATURE_MOSAIK_HTML } from './mails.js';
import { MODELES_MAIL, modeleMail } from './modeles-mail.js';
import { nomTransporteur, transporteurDepuisTracking } from './suivi.js';
/**
 * Relance fournisseur — à qui écrire, dans quelle langue, avec quel texte.
 *
 * Trois choses vivent ici, en fonctions pures :
 *
 * - l'**annuaire fournisseurs** (adresses et langue), repris de la liste tenue
 *   par le service administratif — modifiable ensuite depuis Réglages ;
 * - les **modèles** de relance, un par langue, eux aussi modifiables ;
 * - la **composition** : remplissage des variables, puis mise en HTML.
 *
 * Rien n'est envoyé d'ici : la fenêtre de relance montre le texte composé,
 * l'utilisateur le corrige s'il le faut, et c'est l'API qui l'envoie depuis
 * contact@. Le clic d'envoi reste humain, comme pour le mail de tracking.
 */
/**
 * Annuaire par défaut : société → adresses et langue de correspondance.
 *
 * Les clés sont les noms de fournisseurs du référentiel produits
 * (`Products.csv`), à la normalisation près — `MODULE CARRE ` y figure avec une
 * espace finale, `normaliserSociete` la retire. Les transporteurs et les
 * sociétés sans référence au catalogue y sont aussi : ils ne coûtent rien et
 * évitent d'avoir à ressaisir une adresse le jour où une commande les concerne.
 *
 * Ces valeurs ne sont qu'un point de départ : elles sont recopiées en base au
 * premier démarrage et se corrigent depuis Réglages, sans passer par le code.
 */
export const CONTACTS_FOURNISSEUR_PAR_DEFAUT = {
    CARPENTER: {
        langue: 'fr',
        email: 'nathalie.frenette@carpenter.com, confort.france@carpenter.com, isabelle.mallet@carpenter.com',
    },
    DHL: { langue: 'fr', email: 'christelle.lelong@dhl.com' },
    DPD: { langue: 'fr', email: 'ugo.decampos@dpd.fr' },
    'DR SCHUTZ': { langue: 'fr', email: 'acl@dr-schutz.fr, scl@dr-schutz.fr' },
    FORTEMIX: {
        langue: 'en',
        // Fournisseur au sens propre : c'est lui qui doit la date d'expédition.
        relance: 'fournisseur',
        email: 'expedice@fortemix.cz, orders@fortemix.cz, krejcova@fortemix.com, havel@fortemix.cz, sigmundova@fortemix.cz',
    },
    'FRANCE EXPRESS': { langue: 'fr', email: 'courses.de-fe.valenciennes@geodis.com' },
    GEODIS: { langue: 'fr', email: 'marianne.montorio@geodis.com' },
    // HIOLLE Logistique EST le transport : c'est lui qu'on réclame, pas un
    // fournisseur qui devrait une date d'expédition.
    HIOLLE: { langue: 'fr', email: 'hiollelogistique@hiolle-industries.fr', relance: 'transporteur' },
    IVC: { langue: 'fr', email: 'Marjorie.Vandamme@unilin.com, Thierry.Herrera@unilin.com' },
    'MODULE CARRE': { langue: 'fr', email: 'nbrutin@module-2.com, gbelloni@module-2.com' },
    MOSOLUT: { langue: 'en', email: 'info@mosolut.com, daniel@mosolut.com, jakub@mosolut.com' },
    ONEFLOR: { langue: 'fr', email: 'adv.france@oneflor-europe.com, d.boulanger@oneflor-europe.com' },
    'R-TEK': {
        langue: 'en',
        // Fabrique ET expédie : le numéro de suivi ne viendra d'aucun transporteur,
        // c'est à R-TEK qu'il se réclame.
        expedie: true,
        email: 'karolina.maz@r-tekmanufacturingltd.com, cathal.mckillion@r-tekmanufacturingltd.com, '
            + 'eugene@r-tekmanufacturingltd.com, claire@r-tekmanufacturingltd.com, laura.anderson@r-tekmanufacturingltd.com',
    },
    // DB Schenker a fusionné avec DSV : ses interlocuteurs ont disparu. Ceux de
    // DSV se renseignent dans Réglages.
    DSV: { langue: 'fr', email: '' },
    UZIN: { langue: 'fr', email: 'adv.france@uzin-utz.com' },
    VALINEO: { langue: 'fr', email: 'exploitation@valineoplastic.fr' },
    STILMAT: { langue: 'en', email: 'tadeas.pesek@stilmat.cz' },
    BERGO: {
        langue: 'en',
        email: 'johanna.jannesson@flooring.se, sylwia.turton@flooring.se, peder.stahl@flooring.se',
    },
    // La liste d'origine distingue l'adresse de commande (commande@objectflor.de)
    // des deux interlocuteurs « pour la relance » : ce sont ces derniers qui sont
    // retenus ici, puisque c'est de relance qu'il s'agit.
    OBJECTFLOOR: { langue: 'fr', email: 'a.lefevre@jhfrance.fr, l.mestas@jhfrance.fr' },
};
/**
 * Modèles de relance, un par langue.
 *
 * Écrits en texte brut : c'est ce que l'utilisateur relit et corrige dans la
 * fenêtre, et `texteVersHtml` s'occupe de la mise en forme au moment de l'envoi.
 *
 * Deux tables, parce que deux demandes : `RELANCE_FOURNISSEUR_PAR_DEFAUT`
 * réclame une date d'expédition, `RELANCE_TRACKING_PAR_DEFAUT` réclame le
 * numéro de suivi d'une commande DÉJÀ partie. Employer la première pour la
 * seconde demanderait au fournisseur une date qu'il a déjà donnée.
 *
 * Ni l'une ni l'autre ne récite le détail de la commande. On s'adresse à
 * quelqu'un qui l'a déjà : lui relire ses propres lignes n'aide personne à
 * répondre plus vite. Le seul cas qui le demandait — « je ne la retrouve pas »
 * — est traité par la fenêtre de commande, qui la repasse en entier.
 */
/**
 * Modèles de relance, un par langue.
 *
 * Les textes vivent désormais dans le registre `MODELES_MAIL`, avec tous les
 * autres : un seul endroit où les chercher, et un seul mécanisme de repli.
 * Ces deux exports restent, parce qu'ils sont le nom sous lequel le reste du
 * code les connaît.
 */
export const RELANCE_FOURNISSEUR_PAR_DEFAUT = MODELES_MAIL.relance_fournisseur.defaut;
/**
 * Relance « numéro de suivi », un modèle par langue.
 *
 * Pour une commande PARTIE dont on n'a pas le numéro, chez une société qui
 * expédie elle-même (`expedie` dans l'annuaire). La demande n'est ni une date
 * d'expédition — elle est connue, elle figure dans le mail —, ni une livraison
 * bloquée : le colis roule peut-être très bien, il nous manque de quoi le
 * suivre et prévenir le client.
 *
 * Le transporteur est demandé en même temps que le numéro : sans lui, un numéro
 * seul ne se relève pas.
 */
export const RELANCE_TRACKING_PAR_DEFAUT = MODELES_MAIL.relance_tracking.defaut;
/** Langue appliquée quand la société est absente de l'annuaire. */
export const LANGUE_RELANCE_DEFAUT = 'fr';
/**
 * Clé d'annuaire d'une société : majuscules, espaces normalisées.
 *
 * Le référentiel produits porte `MODULE CARRE ` et `MODULE CARRE` pour le même
 * fournisseur ; sans cette normalisation, l'un des deux perdrait son adresse.
 */
export function normaliserSociete(nom) {
    return String(nom ?? '').trim().replace(/\s+/g, ' ').toUpperCase();
}
/**
 * Contact d'un fournisseur, d'après l'annuaire des réglages.
 *
 * Une société absente ne reçoit pas d'adresse de secours : `email` reste vide
 * et `connu` vaut `false`. La fenêtre de relance le dit et laisse saisir
 * l'adresse — un mail parti à une adresse devinée se perd sans bruit.
 */
export function contactFournisseur(reglages, societe) {
    const nom = String(societe ?? '').trim();
    const fiche = reglages.contactsFournisseur?.[normaliserSociete(nom)];
    if (!fiche)
        return { societe: nom, email: '', langue: LANGUE_RELANCE_DEFAUT, connu: false };
    return {
        societe: nom,
        email: fiche.email ?? '',
        langue: fiche.langue ?? LANGUE_RELANCE_DEFAUT,
        connu: true,
    };
}
/**
 * Destinataires d'une relance transporteur.
 *
 * **Ce sont ceux qui ont reçu la commande, pas le transporteur.** Les
 * transporteurs que Mosaik croise n'ont pas de service litige : le contrat de
 * transport n'est pas le nôtre, il est celui du fournisseur, qui a choisi son
 * transporteur et reste le seul à pouvoir le faire bouger. Écrire à une adresse
 * générique de transporteur — quand elle existe — revient à écrire à personne.
 *
 * Les adresses sont donc exactement celles du mail de commande : mêmes
 * interlocuteurs, même fil, ils savent de quel envoi on parle.
 *
 * Une commande peut porter plusieurs fournisseurs, et un fournisseur plusieurs
 * adresses. Les doublons sont retirés en ignorant la casse, l'ordre de la
 * commande est conservé — c'est celui que l'utilisateur voit sur la fiche.
 *
 * **Sauf chez HIOLLE, une fois l'envoi parti.** HIOLLE Logistique stocke et
 * fait enlever : le colis remis, c'est le transporteur qui le tient, et c'est
 * lui qu'on relance directement — à l'adresse que l'annuaire lui donne (DHL,
 * DPD, DSV…). L'envoi est parti dès qu'il a une date d'expédition OU un
 * numéro de suivi, et le transporteur se lit au besoin sur ce numéro : exiger
 * les deux champs saisis renvoyait la relance chez HIOLLE, qui n'y peut rien.
 * Avant le départ, ou sans transporteur identifiable, HIOLLE reste le
 * destinataire : c'est lui qui doit faire partir l'envoi.
 *
 * `inconnus` nomme les sociétés absentes de l'annuaire. Rien n'est inventé pour
 * elles : la fenêtre le dit et laisse saisir l'adresse, parce qu'un mail parti
 * à une adresse devinée se perd sans bruit. Un transporteur sans adresse n'est
 * donc PAS remplacé par HIOLLE : il est nommé, et l'adresse se saisit.
 */
export function destinatairesRelanceTransporteur(reglages, fournisseurs, envoi = {}) {
    const adresses = [];
    const vues = new Set();
    const inconnus = [];
    // « Autre » ne nomme personne : le numéro de suivi en dit alors plus.
    // « DB Schenker », enregistré avant la fusion, se relance chez DSV.
    const saisi = nomTransporteur(envoi.transporteur) ?? '';
    const transporteur = saisi && normaliserSociete(saisi) !== 'AUTRE'
        ? saisi
        : (transporteurDepuisTracking(envoi.tracking)?.transporteur ?? '');
    const parti = (Boolean(envoi.dateExpedition) || Boolean(String(envoi.tracking ?? '').trim()))
        && Boolean(transporteur) && normaliserSociete(transporteur) !== 'HIOLLE';
    for (const societe of fournisseurs ?? []) {
        const fournisseur = String(societe ?? '').trim();
        if (!fournisseur)
            continue;
        const nom = parti && normaliserSociete(fournisseur) === 'HIOLLE'
            ? societeTransporteur(reglages, transporteur)
            : fournisseur;
        const contact = contactFournisseur(reglages, nom);
        const trouvees = decouperAdresses(contact.email);
        if (!trouvees.length) {
            if (!inconnus.includes(nom))
                inconnus.push(nom);
            continue;
        }
        for (const adresse of trouvees) {
            const cle = adresse.toLowerCase();
            if (vues.has(cle))
                continue;
            vues.add(cle);
            adresses.push(adresse);
        }
    }
    return { destinataires: adresses.join(', '), inconnus };
}
/**
 * Nom d'annuaire d'un transporteur, tel que le suivi l'écrit.
 *
 * Le suivi peut dire « DB Dupont » quand l'annuaire dit « DUPONT » : sans ce
 * pont, la relance réclamerait une adresse que l'annuaire a déjà.
 * Un transporteur qu'aucune forme ne trouve est rendu tel quel, pour que la
 * fenêtre le nomme.
 */
function societeTransporteur(reglages, transporteur) {
    const nom = normaliserSociete(transporteur);
    const sansDb = nom.replace(/^DB /, '');
    if (!reglages.contactsFournisseur?.[nom] && reglages.contactsFournisseur?.[sansDb])
        return sansDb;
    return transporteur;
}
/**
 * Qui relancer pour cette commande : le fournisseur, ou le transport.
 *
 * La règle vit dans l'annuaire, pas dans le code : chaque société y dit ce
 * qu'elle est. `fournisseur` par défaut — c'est le cas courant, et c'est lui qui
 * doit une date d'expédition.
 *
 * **Le premier fournisseur de la commande décide.** Une commande à plusieurs
 * fournisseurs dont les réglages divergeraient n'a pas de bonne réponse
 * automatique ; prendre le premier est prévisible, et l'écran laisse changer de
 * cible d'un clic. Trancher autrement — « transporteur dès qu'un seul le dit » —
 * ferait dépendre le résultat de l'ordre d'une liste sans que cela se voie.
 */
export function cibleRelance(reglages, fournisseurs) {
    for (const societe of fournisseurs ?? []) {
        const nom = String(societe ?? '').trim();
        if (!nom)
            continue;
        return reglages.contactsFournisseur?.[normaliserSociete(nom)]?.relance ?? 'fournisseur';
    }
    return 'fournisseur';
}
/**
 * Cette commande est-elle expédiée par son fournisseur lui-même ?
 *
 * Même règle de lecture que `cibleRelance` — l'annuaire décide, le premier
 * fournisseur de la commande tranche — mais la question n'est pas la même : là
 * où `cibleRelance` dit à QUI écrire, celle-ci dit s'il existe seulement un
 * transporteur à qui réclamer un numéro de suivi. Chez R-TEK il n'y en a pas.
 */
export function expedieLuiMeme(reglages, fournisseurs) {
    for (const societe of fournisseurs ?? []) {
        const nom = String(societe ?? '').trim();
        if (!nom)
            continue;
        return reglages.contactsFournisseur?.[normaliserSociete(nom)]?.expedie === true;
    }
    return false;
}
/** Modèle de relance d'une langue, avec repli sur le français puis sur le défaut. */
export function modeleRelanceFournisseur(reglages, langue, 
/** Ce qu'on réclame. Seul `tracking` change de modèle ; le reste demande une date. */
motif = 'relance') {
    const type = motif === 'tracking' ? 'relance_tracking' : 'relance_fournisseur';
    /*
     * Les modèles vivent dans le registre ; les anciens champs restent lus, en
     * repli, tant qu'ils portent quelque chose.
     *
     * Sans ce repli, le jour du déploiement, les textes corrigés dans Réglages
     * depuis des mois disparaîtraient au profit de ceux du code — et la
     * correction se serait perdue sans que personne ne la voie partir.
     */
    const ancien = (motif === 'tracking' ? reglages.relanceTracking : reglages.relanceFournisseur)?.[langue];
    const registre = modeleMail(reglages, type, langue);
    const enregistre = reglages.modelesMail?.[type]?.[langue];
    return {
        objet: enregistre?.objet?.trim() ? registre.objet
            : (ancien?.objet?.trim() ? ancien.objet : registre.objet),
        corps: enregistre?.corps?.trim() ? registre.corps
            : (ancien?.corps?.trim() ? ancien.corps : registre.corps),
    };
}
/**
 * Unité écrite au fournisseur.
 *
 * Le référentiel ne connaît que deux valeurs, `unité` et `m2` (plus un `m²`
 * isolé et une ligne vide). Tout ce qui n'est pas explicitement une pièce est
 * traité en mètres carrés, comme le faisait le scénario n8n.
 *
 * Les deux langues écrivent la même chose : « piece(s) » et « m² » se lisent
 * partout, et un fournisseur tchèque ne gagnerait rien à recevoir « pièce(s) ».
 */
export function uniteLisible(unite) {
    return String(unite ?? '').trim().toLowerCase() === 'unité' ? 'piece(s)' : 'm²';
}
const MOIS_EN = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
/** Date courte dans la langue du mail : « 8 sept. » en français, « 8 Sep » en anglais. */
export function formatJourRelance(jour, langue) {
    if (langue === 'fr')
        return formatCourt(jour);
    if (!jour)
        return '—';
    const [a, m, j] = jour.split('-');
    const mois = MOIS_EN[Number(m) - 1];
    if (!mois || !a || !j)
        return '—';
    return `${Number(j)} ${mois} ${a}`;
}
/**
 * Les lignes produit, une par ligne de texte.
 *
 * C'est la désignation DU FOURNISSEUR qui est écrite — « ESD CORNERS / Grey /
 * 4 mm », pas « ANGLES MOSAIK ESD - Gris-Clair / 4 mm ». Notre libellé interne
 * ne lui dirait rien, et c'est précisément à lui qu'on demande de retrouver la
 * commande. Notre désignation ne sert que de repli pour les rares références
 * sans correspondance au référentiel : mieux vaut un libellé imparfait que pas
 * de libellé du tout.
 */
/**
 * Les lignes produit, écrites COMME DANS LE MAIL DE COMMANDE.
 *
 * La désignation du fournisseur d'abord, la quantité ensuite : c'est sous cette
 * forme qu'il a reçu la commande, et c'est celle qu'il sait retrouver dans son
 * système. L'ancienne version mettait NOTRE référence en tête — utile à nous,
 * illisible pour lui.
 *
 * Une ligne sans correspondance ne disparaît pas : elle sort avec sa référence
 * brute, visiblement incomplète. La faire disparaître enverrait une commande
 * amputée sans que rien ne le montre dans le texte qu'on relit.
 */
function listerArticles(articles, langue) {
    if (!articles.length)
        return langue === 'fr' ? '(aucune ligne produit)' : '(no order line)';
    return articles
        .map((a) => {
        // Quantité ET unité : « 12 » ne dit pas si ce sont des dalles ou des m².
        const quantite = a.quantite === null || a.quantite === undefined
            ? ''
            : ` : ${a.quantite} ${uniteLisible(a.unite)}`;
        const reference = a.reference ?? (langue === 'fr' ? 'référence absente' : 'missing reference');
        const libelle = a.correspondance ?? a.nom;
        // Sans désignation fournisseur, la référence reste — suivie de la nôtre
        // entre parenthèses quand elle existe, pour qu'on sache de quoi il s'agit.
        return libelle ? `- ${libelle}${quantite}` : `- ${reference}${quantite}`;
    })
        .join('\n');
}
/**
 * Valeurs des variables du modèle. Les clés sont écrites avec leurs accolades :
 * c'est sous cette forme qu'elles apparaissent dans le texte, et l'utilisateur
 * qui relit le modèle dans Réglages les retrouve telles quelles.
 */
export function valeursRelanceFournisseur(d, langue) {
    const attente = d.dateEnvoiFournisseur ? joursEntre(d.dateEnvoiFournisseur, d.aujourdhui) : null;
    const depuis = d.dateExpedition ? joursEntre(d.dateExpedition, d.aujourdhui) : null;
    return {
        '{{ref}}': d.ref,
        '{{expediee}}': formatJourRelance(d.dateExpedition, langue),
        '{{depuis}}': depuis === null ? '—' : String(depuis),
        '{{fournisseur}}': d.societe || (langue === 'fr' ? 'le fournisseur' : 'the supplier'),
        '{{client}}': d.client,
        '{{ville}}': d.ville ?? '—',
        '{{reception}}': formatJourRelance(d.dateReception, langue),
        '{{transmise}}': formatJourRelance(d.dateEnvoiFournisseur, langue),
        '{{attente}}': attente === null ? '—' : String(attente),
        '{{note}}': d.note ?? '—',
        '{{articles}}': listerArticles(d.articles, langue),
        /*
         * `{{articles}}` et `{{adresse}}` ne figurent plus dans AUCUN modèle par
         * défaut : une relance réclame une date ou un numéro à quelqu'un qui a déjà
         * la commande, réciter ses lignes n'ajoutait que du bruit. Le seul cas qui
         * les justifiait — le fournisseur ne retrouve pas la commande — passe par la
         * fenêtre de COMMANDE, qui renvoie le mail complet.
         *
         * Les deux variables restent servies : les modèles se modifient depuis
         * Réglages, et une variable qui ne répond plus partirait en clair dans un
         * mail chez le fournisseur.
         */
        '{{adresse}}': d.adresse
            ? blocAdresseLivraison({ adresse: d.adresse, entreprise: d.entreprise ?? null, client: d.client, telephone: d.telephone ?? null }, langue === 'fr'
                ? { adresse: 'Adresse de livraison :', telephone: 'Téléphone' }
                : { adresse: 'Delivery address:', telephone: 'Phone' })
            : '',
    };
}
/** Remplace les variables `{{…}}` d'un texte. Une variable inconnue reste en clair. */
export function remplirModele(texte, valeurs) {
    return Object.entries(valeurs).reduce((acc, [cle, valeur]) => acc.split(cle).join(valeur), texte);
}
/**
 * Compose la relance : choisit la langue d'après l'annuaire, remplit le modèle.
 *
 * Fonction pure, appelée par le front pour préremplir la fenêtre. L'API ne la
 * rejoue pas : elle envoie exactement le texte qui a été relu, corrections
 * comprises.
 */
export function composerRelanceFournisseur(reglages, d, 
/**
 * Ce qu'on réclame. `relance` par défaut, c'est-à-dire une date d'expédition :
 * le comportement d'origine, que les appelants existants gardent sans rien
 * changer. Seul `tracking` bascule sur l'autre table de modèles.
 */
motif = 'relance') {
    const contact = contactFournisseur(reglages, d.societe);
    const modele = modeleRelanceFournisseur(reglages, contact.langue, motif);
    const valeurs = valeursRelanceFournisseur(d, contact.langue);
    return {
        langue: contact.langue,
        destinataires: contact.email,
        objet: remplirModele(modele.objet, valeurs),
        // Les lignes vides en trop sont resserrées : une commande sans adresse
        // connue laisserait sinon un trou de trois lignes au milieu du mail.
        corps: remplirModele(modele.corps, valeurs).replace(/\n{3,}/g, '\n\n').trim(),
        contactConnu: contact.connu,
    };
}
/**
 * Texte brut vers document HTML, signature comprise.
 *
 * Une ligne vide sépare deux paragraphes, un simple retour à la ligne devient
 * un `<br>`, et une suite de lignes commençant par « - » devient une vraie
 * liste à puces — c'est sous cette forme que le fournisseur lit ses articles,
 * et c'est ce que faisait le scénario n8n.
 *
 * Les adresses `http(s)` deviennent des liens cliquables : c'est ce qui permet
 * de faire relire un mail de suivi en texte sans lui retirer ce qui en fait
 * l'intérêt. Une URL collée en clair dans du HTML n'est pas un lien.
 *
 * Tout est échappé : le texte vient d'un champ libre, il n'a aucune raison de
 * pouvoir ouvrir une balise.
 */
export function texteVersHtml(texte) {
    const blocs = String(texte ?? '')
        .replace(/\r\n/g, '\n')
        .split(/\n{2,}/)
        .map((p) => p.trim())
        .filter(Boolean)
        .map(rendreBloc)
        .join('\n    ');
    /*
     * La signature HTML est jointe ici, à l'unique point de rendu des quatre
     * mails envoyés. Ce sont les lignes de signature en TEXTE des
     * modèles qui ont été retirées — elles répétaient en clair, juste au-dessus,
     * ce que ce bloc porte déjà.
     */
    return `<!DOCTYPE html>
<html>
  <body style="font-family: Arial, sans-serif; font-size: 14px; color: #333;">
    ${blocs}
    ${SIGNATURE_MOSAIK_HTML}
  </body>
</html>`;
}
/** Un paragraphe, ou une liste à puces si toutes ses lignes commencent par « - ». */
function rendreBloc(bloc) {
    const lignes = bloc.split('\n').map((l) => l.trim()).filter(Boolean);
    const puces = lignes.every((l) => /^[-*•]\s+/.test(l));
    if (puces && lignes.length) {
        const items = lignes
            .map((l) => `<li>${lier(l.replace(/^[-*•]\s+/, ''))}</li>`)
            .join('\n      ');
        return `<ul style="list-style-type: disc;">\n      ${items}\n    </ul>`;
    }
    return `<p>${lignes.map(lier).join('<br>\n    ')}</p>`;
}
/**
 * Échappe une ligne, et transforme ses URL en liens.
 *
 * L'échappement a lieu d'abord, morceau par morceau : une URL est reconstruite
 * à partir du texte échappé, si bien qu'aucun caractère du champ libre ne peut
 * s'échapper de l'attribut `href`. Seuls `http` et `https` sont liés.
 */
function lier(ligne) {
    const morceaux = ligne.split(/(https?:\/\/[^\s<>"']+)/g);
    return morceaux
        .map((morceau, i) => {
        const echappe = echapperHtml(morceau);
        if (i % 2 === 0)
            return echappe;
        return `<a href="${echappe}" style="color: #1a73e8;">${echappe}</a>`;
    })
        .join('');
}
/**
 * Découpe une liste d'adresses saisie à la main.
 *
 * Virgules, points-virgules et retours à la ligne séparent indifféremment :
 * l'annuaire d'origine mélange les trois.
 */
export function decouperAdresses(liste) {
    return String(liste ?? '')
        .split(/[,;\n]/)
        .map((a) => a.trim())
        .filter(Boolean);
}
/**
 * Ce qu'une relance réclame, d'après l'état de la commande.
 *
 * L'écran n'a pas à le décider dans son coin, et le serveur non plus : ce serait
 * deux réponses pour une question, qui divergeraient au premier correctif. La
 * règle est courte et ne dépend PAS de qui l'on relance — on peut réclamer une
 * date d'expédition à un transporteur (HIOLLE Logistique *est* le transport) ou
 * une livraison à un fournisseur. Ce qui la fixe, c'est là où la commande est
 * arrêtée :
 *
 * - le fournisseur ne l'a jamais enregistrée → rien n'est en route, c'est la
 *   commande elle-même qu'on réclame ;
 * - pas de date d'expédition → c'est la date qu'on réclame ;
 * - partie, sans numéro de suivi, chez une société qui expédie elle-même → il
 *   n'existe aucun transporteur à qui le demander, c'est le NUMÉRO qu'on
 *   réclame, et au fournisseur ;
 * - expédiée → c'est la livraison.
 *
 * L'avant-dernier cas est le seul qui ait besoin de l'annuaire, d'où
 * `expedieur`. Sans lui la fonction retombe sur son comportement d'origine, ce
 * qui laisse les appelants qui ne connaissent pas les réglages inchangés — mais
 * leur fait composer un mail de livraison bloquée sur une commande dont on
 * ignore simplement le numéro. Les appelants de l'application le passent tous.
 */
export function motifRelance(c, 
/** La commande est-elle expédiée par son fournisseur ? Cf. `expedieLuiMeme`. */
expedieur = false) {
    if (c.absentPortailFournisseur)
        return 'probleme';
    if (!c.dateExpedition)
        return 'relance';
    if (expedieur && !String(c.tracking ?? '').trim())
        return 'tracking';
    return 'livraison';
}
//# sourceMappingURL=relances.js.map